import React from 'react';

import {Owner} from 'auth/PermissionConstants';
import navigateToURL from 'lib/navigateToURL';
import notifications from 'notifications';
import {ClientSchemaField} from 'registry/field/FieldTable';
import {sanitizeFields} from 'registry/RegistryUtil';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {BenchlingAppSummary} from 'schemas/SchemaConstants';
import {getSchemaOwnerChoices} from 'schemas/spec/SchemaSpecUtil';
import nameToSqlIdentifier from 'warehouse/nameToSqlIdentifier';

export function getOwnerById(ownerId: string | null): Owner | null {
  if (ownerId == null) {
    return null;
  }
  return getSchemaOwnerChoices().find((owner) => owner.id === ownerId) || null;
}

export function findDuplicateField(
  fields: Array<{name: string; systemName: string}>,
  {enforceUniqueDisplayName = true}: {enforceUniqueDisplayName?: boolean} = {}
): {name: string; systemName: string} | null {
  const seenDisplayNames = new Set<string>();
  const seenSystemNames = new Set<string>();
  for (const field of fields) {
    const duplicateDisplayName = enforceUniqueDisplayName && seenDisplayNames.has(field.name);
    const duplicateSystemName = seenSystemNames.has(field.systemName);
    if (duplicateDisplayName || duplicateSystemName) {
      return field;
    }
    seenDisplayNames.add(field.name);
    seenSystemNames.add(field.systemName);
  }
  return null;
}

export function notifyDuplicateField(field: {name: string; systemName: string}, schemaType: string): void {
  notifications.error(
    `Field ${field.name} (${field.systemName}) cannot have the same display name or system name as another field in ${schemaType} schema. Please note that this includes archived fields.`
  );
}

export function massageFieldsForSubmit(fields: Array<ClientSchemaField | SchemaField>): Array<SchemaField> {
  // Don't submit info on relatedBenchlingApps or appliedFieldset, if any
  // Do not submit unitSymbol, because the client cannot edit a unit's symbol for an individual schema field.
  // Keep the return value in sync with /src/benchling/schemas/specs/json_spec_adapter.py:255
  return sanitizeFields(fields).map(({relatedBenchlingApps, appliedFieldset, unitSymbol, ...field}) => ({
    ...field,
    // Use || instead of ?? to handle the systemName='' case
    systemName: field.systemName || nameToSqlIdentifier(field.displayName),
  }));
}

export function getMessageForRelatedApps(relatedBenchlingApps: Array<BenchlingAppSummary>): JSX.Element {
  const numBenchlingApps = relatedBenchlingApps.length;
  const appUrls = relatedBenchlingApps.slice(0, 3).map((appSummary, i) => (
    <span key={i}>
      {i > 0 && ', '}
      <a
        onClick={() => {
          navigateToURL(appSummary.url);
        }}
      >
        {appSummary.name}
      </a>
    </span>
  ));
  let relatedBenchlingAppsMsg;
  if (numBenchlingApps > 3) {
    relatedBenchlingAppsMsg = (
      <>
        {appUrls} + {numBenchlingApps - 3} more
      </>
    );
  } else {
    relatedBenchlingAppsMsg = <>{appUrls}</>;
  }
  return <span>Referenced by app config(s) for: {relatedBenchlingAppsMsg}</span>;
}
